import { getSPDataWithParams, postSPDataWithParams, putSPDataWithParams } from 'src/supportPortal/services/services'

/**
 * API service layer for KYC approval operations.
 * All endpoints follow the backend API contracts.
 */

const BASE_PATH = '/api/secure/cc/support/control/kyc/therapists'

export const kycApprovalAPI = {
  // ==================== List & Get Operations ====================

  /**
   * List all approval requests with optional status filtering
   * @param {string} status - Optional filter: REQUESTED, IN_REVIEW, PARTIALLY_APPROVED, APPROVED, REJECTED
   * @returns {Promise} List of approval requests with therapist details
   */
  listRequests: async (status = null) => {
    const params = status ? { status } : {}
    return await getSPDataWithParams(`${BASE_PATH}/request`, params)
  },

  /**
   * Get detailed view of a single approval request with all section data
   * @param {string} requestId - The approval request ID
   * @returns {Promise} Complete request with all sections and therapist details
   */
  getRequest: async (requestId) => {
    return await getSPDataWithParams(`${BASE_PATH}/requests/${requestId}`)
  },

  // ==================== Create Operation ====================

  /**
   * Create a new approval request for a therapist
   * @param {string} therapistId - The therapist ID
   * @param {string} applicationRef - Application reference number (e.g., APP-2025-0001)
   * @returns {Promise} Created request summary
   */
  createRequest: async (therapistId, applicationRef) => {
    return await postSPDataWithParams(`${BASE_PATH}/request`, {
      therapistId,
      applicationRef,
    })
  },

  // ==================== Section Save Operations ====================

  /**
   * Save KYC section data
   * @param {string} requestId - The approval request ID
   * @param {object} data - KYC section data
   * @param {string} transition - Optional: DRAFT or SUBMITTED (default: DRAFT)
   * @returns {Promise} Updated request summary
   */
  saveKyc: async (requestId, data, transition = null) => {
    const params = { requestId }
    if (transition) params.transition = transition
    return await putSPDataWithParams(`${BASE_PATH}/requests/sections/kyc`, data, params)
  },

  /**
   * Save Functions section data
   */
  saveFunctions: async (requestId, data, transition = null) => {
    const params = { requestId }
    if (transition) params.transition = transition
    return await putSPDataWithParams(`${BASE_PATH}/requests/sections/functions`, data, params)
  },

  /**
   * Save Assignments section data
   */
  saveAssignments: async (requestId, data, transition = null) => {
    const params = { requestId }
    if (transition) params.transition = transition
    return await putSPDataWithParams(`${BASE_PATH}/requests/sections/assignments`, data, params)
  },

  /**
   * Save Permissions section data
   */
  savePermissions: async (requestId, data, transition = null) => {
    const params = { requestId }
    if (transition) params.transition = transition
    return await putSPDataWithParams(`${BASE_PATH}/requests/sections/permissions`, data, params)
  },

  /**
   * Save Payout section data
   */
  savePayout: async (requestId, data, transition = null) => {
    const params = { requestId }
    if (transition) params.transition = transition
    return await putSPDataWithParams(`${BASE_PATH}/requests/sections/payout`, data, params)
  },

  /**
   * Save Ops section data
   */
  saveOps: async (requestId, data, transition = null) => {
    const params = { requestId }
    if (transition) params.transition = transition
    return await putSPDataWithParams(`${BASE_PATH}/requests/sections/ops`, data, params)
  },

  // ==================== Section Approve Operations ====================

  /**
   * Approve KYC section
   */
  approveKyc: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/kyc/approve`, {}, { requestId })
  },

  /**
   * Approve Functions section
   */
  approveFunctions: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/functions/approve`, {}, { requestId })
  },

  /**
   * Approve Assignments section
   */
  approveAssignments: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/assignments/approve`, {}, { requestId })
  },

  /**
   * Approve Permissions section
   */
  approvePermissions: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/permissions/approve`, {}, { requestId })
  },

  /**
   * Approve Payout section
   */
  approvePayout: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/payout/approve`, {}, { requestId })
  },

  /**
   * Approve Ops section
   */
  approveOps: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/ops/approve`, {}, { requestId })
  },

  // ==================== Section Reject Operations ====================

  /**
   * Reject KYC section
   */
  rejectKyc: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/kyc/reject`, {}, { requestId })
  },

  /**
   * Reject Functions section
   */
  rejectFunctions: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/functions/reject`, {}, { requestId })
  },

  /**
   * Reject Assignments section
   */
  rejectAssignments: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/assignments/reject`, {}, { requestId })
  },

  /**
   * Reject Permissions section
   */
  rejectPermissions: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/permissions/reject`, {}, { requestId })
  },

  /**
   * Reject Payout section
   */
  rejectPayout: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/payout/reject`, {}, { requestId })
  },

  /**
   * Reject Ops section
   */
  rejectOps: async (requestId) => {
    return await postSPDataWithParams(`${BASE_PATH}/requests/sections/ops/reject`, {}, { requestId })
  },

  // ==================== Helper: Save & Approve All ====================

  /**
   * Save and approve all visible sections in one operation
   * Saves all sections with transition=SUBMITTED, then approves each one
   * @param {string} requestId - The approval request ID
   * @param {object} formData - Object with section data: { kyc: {...}, functions: {...}, ... }
   * @param {string[]} visibleSections - Array of section keys to save/approve
   * @returns {Promise} Success/error result
   */
  saveAndApproveAll: async (requestId, formData, visibleSections) => {
    const sectionMap = {
      kyc: { save: kycApprovalAPI.saveKyc, approve: kycApprovalAPI.approveKyc },
      functions: { save: kycApprovalAPI.saveFunctions, approve: kycApprovalAPI.approveFunctions },
      assignments: { save: kycApprovalAPI.saveAssignments, approve: kycApprovalAPI.approveAssignments },
      permissions: { save: kycApprovalAPI.savePermissions, approve: kycApprovalAPI.approvePermissions },
      payout: { save: kycApprovalAPI.savePayout, approve: kycApprovalAPI.approvePayout },
      ops: { save: kycApprovalAPI.saveOps, approve: kycApprovalAPI.approveOps },
    }

    try {
      // Step 1: Save all sections with SUBMITTED transition
      for (const sectionKey of visibleSections) {
        const sectionData = formData[sectionKey]
        if (sectionData && sectionMap[sectionKey]) {
          await sectionMap[sectionKey].save(requestId, sectionData, 'SUBMITTED')
        }
      }

      // Step 2: Approve all sections
      for (const sectionKey of visibleSections) {
        if (sectionMap[sectionKey]) {
          await sectionMap[sectionKey].approve(requestId)
        }
      }

      return { success: true }
    } catch (error) {
      console.error('saveAndApproveAll failed:', error)
      throw error
    }
  },
}
